import type {
  AddCollectionGroupSnapshotListenerCallback,
  AddCollectionGroupSnapshotListenerOptions,
  AddCollectionSnapshotListenerCallback,
  AddCollectionSnapshotListenerOptions,
  AddDocumentOptions,
  AddDocumentResult,
  AddDocumentSnapshotListenerCallback,
  AddDocumentSnapshotListenerOptions,
  CallbackId,
  DeleteDocumentOptions,
  DocumentData,
  EnablePersistenceOptions,
  FirebaseFirestorePlugin,
  GetCollectionGroupOptions,
  GetCollectionGroupResult,
  GetCollectionOptions,
  GetCollectionResult,
  GetCountFromServerOptions,
  GetCountFromServerResult,
  GetDocumentOptions,
  GetDocumentResult,
  RemoveSnapshotListenerOptions,
  SetDocumentOptions,
  UpdateDocumentOptions,
  UseEmulatorOptions,
  WriteBatchOptions,
} from './definitions';
import { DocumentReference } from './document-reference';
import { deserializeData, serializeData } from './utils';

export class FirebaseFirestoreClient implements FirebaseFirestorePlugin {
  private readonly plugin: FirebaseFirestorePlugin;

  constructor(plugin: FirebaseFirestorePlugin) {
    this.plugin = plugin;
  }

  async addCollectionGroupSnapshotListener<
    T extends DocumentData = DocumentData,
  >(
    options: AddCollectionGroupSnapshotListenerOptions,
    callback: AddCollectionGroupSnapshotListenerCallback<T>,
  ): Promise<CallbackId> {
    return this.plugin.addCollectionGroupSnapshotListener<T>(
      serializeData(options),
      (event, error) => {
        if (event) {
          for (const snapshot of event.snapshots) {
            if (snapshot.data !== null) {
              snapshot.data = deserializeData(snapshot.data);
            }
          }
        }
        callback(event, error);
      },
    );
  }

  async addCollectionSnapshotListener<T extends DocumentData = DocumentData>(
    options: AddCollectionSnapshotListenerOptions,
    callback: AddCollectionSnapshotListenerCallback<T>,
  ): Promise<CallbackId> {
    return this.plugin.addCollectionSnapshotListener<T>(
      serializeData(options),
      (event, error) => {
        if (event) {
          for (const snapshot of event.snapshots) {
            if (snapshot.data !== null) {
              snapshot.data = deserializeData(snapshot.data);
            }
          }
        }
        callback(event, error);
      },
    );
  }

  async addDocument(options: AddDocumentOptions): Promise<AddDocumentResult> {
    const result = await this.plugin.addDocument({
      ...options,
      data: serializeData(options.data),
    });
    result.reference = DocumentReference.fromPath(result.reference.path);
    return result;
  }

  async addDocumentSnapshotListener<T extends DocumentData = DocumentData>(
    options: AddDocumentSnapshotListenerOptions,
    callback: AddDocumentSnapshotListenerCallback<T>,
  ): Promise<CallbackId> {
    return this.plugin.addDocumentSnapshotListener<T>(
      options,
      (event, error) => {
        if (
          event?.snapshot.data !== null &&
          event?.snapshot.data !== undefined
        ) {
          event.snapshot.data = deserializeData(event.snapshot.data);
        }
        callback(event, error);
      },
    );
  }

  async clearPersistence(): Promise<void> {
    return this.plugin.clearPersistence();
  }

  async deleteDocument(options: DeleteDocumentOptions): Promise<void> {
    return this.plugin.deleteDocument(options);
  }

  async disableNetwork(): Promise<void> {
    return this.plugin.disableNetwork();
  }

  async disablePersistence(): Promise<void> {
    return this.plugin.disablePersistence();
  }

  async enableNetwork(): Promise<void> {
    return this.plugin.enableNetwork();
  }

  async enablePersistence(options?: EnablePersistenceOptions): Promise<void> {
    return this.plugin.enablePersistence(options);
  }

  async getCollection<T extends DocumentData = DocumentData>(
    options: GetCollectionOptions,
  ): Promise<GetCollectionResult<T>> {
    const result = await this.plugin.getCollection<T>(serializeData(options));
    for (const snapshot of result.snapshots) {
      if (snapshot.data !== null) {
        snapshot.data = deserializeData(snapshot.data);
      }
    }
    return result;
  }

  async getCollectionGroup<T extends DocumentData = DocumentData>(
    options: GetCollectionGroupOptions,
  ): Promise<GetCollectionGroupResult<T>> {
    const result = await this.plugin.getCollectionGroup<T>(
      serializeData(options),
    );
    for (const snapshot of result.snapshots) {
      if (snapshot.data !== null) {
        snapshot.data = deserializeData(snapshot.data);
      }
    }
    return result;
  }

  async getCountFromServer(
    options: GetCountFromServerOptions,
  ): Promise<GetCountFromServerResult> {
    return this.plugin.getCountFromServer(options);
  }

  async getDocument<T extends DocumentData = DocumentData>(
    options: GetDocumentOptions,
  ): Promise<GetDocumentResult<T>> {
    const result = await this.plugin.getDocument<T>(options);
    if (result.snapshot.data !== null) {
      result.snapshot.data = deserializeData(result.snapshot.data);
    }
    return result;
  }

  async removeAllListeners(): Promise<void> {
    return this.plugin.removeAllListeners();
  }

  async removeSnapshotListener(
    options: RemoveSnapshotListenerOptions,
  ): Promise<void> {
    return this.plugin.removeSnapshotListener(options);
  }

  async setDocument(options: SetDocumentOptions): Promise<void> {
    return this.plugin.setDocument({
      ...options,
      data: serializeData(options.data),
    });
  }

  async updateDocument(options: UpdateDocumentOptions): Promise<void> {
    return this.plugin.updateDocument({
      ...options,
      data: serializeData(options.data),
    });
  }

  async useEmulator(options: UseEmulatorOptions): Promise<void> {
    return this.plugin.useEmulator(options);
  }

  async writeBatch(options: WriteBatchOptions): Promise<void> {
    return this.plugin.writeBatch({
      ...options,
      operations: options.operations.map(op => ({
        ...op,
        data: op.data ? serializeData(op.data) : op.data,
      })),
    });
  }
}
